import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { Box, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { UserPermissionDto } from '@/api/generated';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useKnowMeNavigate } from '@/utils/useKnowMeNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

import {
  getGlobalAiAssistantPath,
  isProfileDevelopmentPath,
} from './util/getGlobalAiAssistantPath.util.ts';

export const GlobalAiAssistantFab: React.FC = () => {
  const { colors } = useTheme();
  const { t } = useTranslationWithPrefix('views.landingPage');
  const { permissions } = useUserContext();
  const { push } = useKnowMeNavigate();
  const label = t('dashboard.aiAssistant.label');
  const hasSelfProfilePermission = permissions.includes(UserPermissionDto.EmployeesSelfProfile);
  const location = useLocation();
  const isInDevelopment = isProfileDevelopmentPath(location.pathname);
  const isInAdminPanel = location.pathname.startsWith('/admin');

  if (!hasSelfProfilePermission || isInDevelopment || isInAdminPanel) return null;

  return (
    <Box
      animate={{ opacity: 1, y: 0 }}
      component={motion.div as React.ElementType}
      initial={{ opacity: 0, y: 20 }}
      sx={{
        bottom: { sm: 28, xs: 18 },
        position: 'fixed',
        right: { sm: 28, xs: 18 },
        zIndex: 1250,
      }}
      transition={{ delay: 0.45, duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
    >
      <Box
        aria-label={label}
        component="button"
        onClick={() => push(getGlobalAiAssistantPath(location.pathname))}
        sx={{
          '&:hover': {
            boxShadow: `0 12px 32px rgba(0,0,0,0.42), ${colors.shadowGoldGlow}`,
            gap: 1,
            maxWidth: 238,
            px: 2.15,
            transform: 'translateY(-2px)',
          },
          '&:hover .ai-fab-label': {
            maxWidth: 164,
            opacity: 1,
          },
          '& .ai-fab-label': {
            maxWidth: 0,
            opacity: 0,
          },
          alignItems: 'center',
          appearance: 'none',
          background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
          border: `1px solid ${colors.accentBorder}`,
          borderRadius: '28px',
          boxShadow: `${colors.shadowGoldGlow}, 0 1px 4px rgba(0,0,0,0.3)`,
          color: colors.darkCharcoal,
          cursor: 'pointer',
          display: 'flex',
          gap: 0,
          height: { sm: 56, xs: 52 },
          justifyContent: 'center',
          maxWidth: { sm: 56, xs: 52 },
          minWidth: { sm: 56, xs: 52 },
          overflow: 'hidden',
          px: 0,
          transition:
            'max-width 0.3s cubic-bezier(0.4,0,0.2,1), padding 0.3s cubic-bezier(0.4,0,0.2,1), gap 0.3s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s cubic-bezier(0.4,0,0.2,1)',
        }}
        type="button"
      >
        <Box
          className="ai-fab-icon"
          sx={{
            '& .MuiSvgIcon-root': {
              fontSize: 24,
              mr: 0.3,
              transform: 'translate(0.5px, -0.5px)',
            },
            alignItems: 'center',
            color: colors.darkCharcoal,
            display: 'flex',
            flexShrink: 0,
            height: 32,
            justifyContent: 'center',
            width: 32,
          }}
        >
          <AutoAwesomeRoundedIcon />
        </Box>
        <Typography
          className="ai-fab-label"
          sx={{
            color: colors.darkCharcoal,
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1,
            overflow: 'hidden',
            transition:
              'max-width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s cubic-bezier(0.4,0,0.2,1)',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};
